export interface Coordinates {
  lat: number;
  lng: number;
  accuracy?: number | null;
}

export const getCurrentPosition = (): Promise<Coordinates> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(
        new Error(
          'Location is not supported by your browser. Use a device with location services to check in.'
        )
      );
      return;
    }

    const onSuccess = (position: GeolocationPosition) => {
      resolve({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
      });
    };

    const onError = (error: GeolocationPositionError, canRetry: boolean) => {
      if (error.code === error.PERMISSION_DENIED) {
        reject(
          new Error('Location access was denied. Enable location for this site and try again.')
        );
      } else if (canRetry) {
        // GPS can take longer to acquire a fix indoors or on a mobile network.
        // A second request lets the device use Wi-Fi or cell positioning.
        navigator.geolocation.getCurrentPosition(
          onSuccess,
          (retryError) => onError(retryError, false),
          {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 30000,
          }
        );
      } else {
        reject(
          new Error(
            'Could not get your location. Turn on device location, move to an area with better signal, and try check-in again.'
          )
        );
      }
    };

    navigator.geolocation.getCurrentPosition(onSuccess, (error) => onError(error, true), {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 30000,
    });
  });
};

export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

export const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
};
